import { ICartProduct } from '@/types';
import { getProductMaxInstallmentsFromCharacteristics } from '@/lib/utils/product';

export type InstallmentTier = {
  minValue: number;
  maxInstallments: number;
};

type InstallmentOption = {
  value: number;
  label: string;
};

function normalizeInstallmentRules(
  installmentRules: InstallmentTier[],
): InstallmentTier[] {
  return installmentRules.map((tier) => ({
    minValue: Number(tier.minValue),
    maxInstallments: Number(tier.maxInstallments),
  }));
}

function getCartLineAmount(item: ICartProduct): number {
  const unitPrice = item.calculated_sale_price ?? item.sale_price ?? 0;
  return unitPrice * (item.quantity ?? 1);
}

export function getInstallmentTier(
  amount: number,
  installmentRules: InstallmentTier[],
): InstallmentTier {
  const rules = normalizeInstallmentRules(installmentRules);

  return (
    rules
      .slice()
      .reverse()
      .find((tier) => amount >= tier.minValue) ?? {
      minValue: 0,
      maxInstallments: 1,
    }
  );
}

/**
 * Resolves checkout max installments when the cart mixes global installment
 * rules (settings) with per-product MAX_CARD_INSTALLMENTS characteristics.
 *
 * A product characteristic overrides the settings tier, so it can raise the
 * cart maximum above what the order total alone would allow.
 */
export function resolveCheckoutMaxInstallments(
  items: ICartProduct[],
  totalAmount: number,
  installmentRules: InstallmentTier[],
): number {
  const characteristicCaps: number[] = [];
  let nonCharacteristicSubtotal = 0;

  for (const item of items) {
    const productMaxInstallments = getProductMaxInstallmentsFromCharacteristics(
      item.characteristics,
    );

    if (productMaxInstallments !== undefined) {
      characteristicCaps.push(productMaxInstallments);
      continue;
    }

    nonCharacteristicSubtotal += getCartLineAmount(item);
  }

  if (characteristicCaps.length === 0) {
    return getInstallmentTier(totalAmount, installmentRules).maxInstallments;
  }

  const characteristicCap = Math.max(...characteristicCaps);

  if (nonCharacteristicSubtotal <= 0) {
    return characteristicCap;
  }

  const nonCharacteristicCap = getInstallmentTier(
    nonCharacteristicSubtotal,
    installmentRules,
  ).maxInstallments;

  return Math.max(characteristicCap, nonCharacteristicCap);
}

/**
 * Max installments for a single product (PDP / card).
 *
 * The MAX_CARD_INSTALLMENTS characteristic is authoritative: when the product
 * defines it, it replaces the settings tier instead of only capping it.
 */
export function resolveProductMaxInstallments(
  salePrice: number,
  installmentRules: InstallmentTier[],
  maxInstallmentsFromCharacteristic?: number,
): number {
  if (maxInstallmentsFromCharacteristic !== undefined) {
    return maxInstallmentsFromCharacteristic;
  }

  return getInstallmentTier(salePrice, installmentRules).maxInstallments;
}

/**
 * `resolvedMaxInstallments` is the already-resolved maximum (from
 * `resolveProductMaxInstallments` / `resolveCheckoutMaxInstallments`) and wins
 * over the settings tier. Falls back to the tier when omitted.
 */
export function generateInstallmentOptions(
  amount: number,
  installmentRules: InstallmentTier[],
  resolvedMaxInstallments?: number,
): InstallmentOption[] {
  const maxInstallments = Math.max(
    1,
    Math.floor(
      resolvedMaxInstallments ??
        getInstallmentTier(amount, installmentRules).maxInstallments,
    ),
  );

  return Array.from({ length: maxInstallments }, (_, i) => i + 1).map(
    (installments) => {
      const value = amount / installments;

      return {
        value: installments,
        label: `${installments}x de R$ ${value.toFixed(2).replace('.', ',')} sem juros`,
      };
    },
  );
}
